import React from 'react';
import OrderIcon from '../../assets/svgs/OrderIcon';
import ProductIcon from '../../assets/svgs/ProductIcon';
import UsersIcon from '../../assets/svgs/UsersIcon';
import { CreateButton } from '../SharedComponents';
import moment from 'moment-timezone';

interface SubscriptionPlan {
	id: number;
	name: string;
	display_name: string;
	billing_interval: 'monthly' | 'annually';
	price: number;
	currency: string;
	features: {
		analytics: boolean;
		emailSupport: boolean;
		invoiceReadReceipts: boolean;
		onlineStore: boolean;
	};
	sales_limit: number | null;
	products_limit: number | null;
	team_members_limit: number | null;
	is_active: boolean;
	created_at: string;
	updated_at: string;
}

interface PlansResponse {
	monthly: SubscriptionPlan[];
	annually: SubscriptionPlan[];
}

interface CurrentSubscriptionData {
	id: number;
	shop_id: number;
	plan_id: number;
	status: 'active' | 'past_due' | 'canceled' | 'pending_downgrade' | 'expired';
	current_period_start: string;
	current_period_end: string;
	next_billing_date: string | null;
	pending_plan_id: number | null;
	pending_plan_name?: string;
	pending_plan_display_name?: string;
	pending_plan_billing_interval?: 'monthly' | 'annually';
	paystack_customer_code: string | null;
	plan_name: string;
	display_name: string;
	price: number;
	features: any;
	is_cancelling?: boolean;
	usage?: {
		sales: number;
		products: number;
		teamMembers: number;
	};
}

interface UsageCardProps {
	icon: React.ReactNode;
	label: string;
	current: number;
	limit: number | null;
	planGradient: string;
}

interface CurrentSubscriptionProps {
	subscription: CurrentSubscriptionData | null;
	plansData: PlansResponse;
	selectedPeriod: 'monthly' | 'annually';
	onCancelClick: () => void;
	onChangePlanClick: () => void;
	onCancelDowngrade: () => void;
	onReactivate: () => void;
	showPlans: boolean;
	currency: string;
	userTimezone: string;
	isTrialing?: boolean;
}

// UsageCard sub-component
const UsageCard: React.FC<UsageCardProps> = ({ icon, label, current, limit, planGradient }) => {
	const percentage =
		limit === null || limit === undefined
			? current === 0
				? 0
				: 5 //this is here just so there's a little progress showing for unlimited limits. Without this, unlimited will always be an empty bar
			: Math.min((current / limit) * 100, 100);

	const displayLimit = limit === null || limit === undefined ? '∞' : limit.toLocaleString();

	return (
		<div className="bg-gray-50 border border-gray-200 rounded-lg p-6 flex flex-col">
			<div className="flex items-center gap-3 mb-2">
				<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">{icon}</div>
				<span className="text-gray-600 text-sm">{label}</span>
			</div>
			<span className="text-gray-900 text-2xl font-semibold mb-3">
				{current}/{displayLimit}
			</span>
			{/* Usage Bar */}
			<div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
				<div
					className={`h-full rounded-full transition-all duration-500 ${planGradient}`}
					style={{ width: `${percentage}%` }}
				/>
			</div>
		</div>
	);
};

const CurrentSubscription: React.FC<CurrentSubscriptionProps> = ({
	subscription,
	plansData,
	selectedPeriod,
	onCancelClick,
	onChangePlanClick,
	onCancelDowngrade,
	onReactivate,
	showPlans,
	currency,
	userTimezone,
	isTrialing,
}) => {
	// Helper function to get plan gradient
	const getPlanGradient = (planType: string) => {
		const plan = planType.toLowerCase();
		if (plan.includes('business') || plan.includes('enterprise')) {
			return 'bg-gradient-to-r from-yellow-700 to-yellow-500';
		} else if (plan.includes('growth')) {
			return 'bg-gradient-to-r from-purple-800 to-purple-500';
		} else {
			return 'bg-gradient-to-r from-blue-800 to-blue-500';
		}
	};

	// Helper function to format dates in user's timezone
	const formatDateInUserTimezone = (dateString: string) => {
		return moment(dateString).tz(userTimezone).format('MMMM D, YYYY');
	};

	// Helper function to check if a plan is the current plan
	const isCurrentPlan = (plan: SubscriptionPlan) => {
		if (!subscription || !subscription.plan_name) return false;
		return subscription.plan_name === plan.name;
	};

	// Find the current plan from plansData
	const findCurrentPlan = () => {
		let currentPlan = plansData.monthly.find((plan) => isCurrentPlan(plan));
		if (!currentPlan) {
			currentPlan = plansData.annually.find((plan) => isCurrentPlan(plan));
		}
		return currentPlan;
	};

	// Helper function to check if cancelled subscription is still active
	const isCancelledButActive = () => {
		if (subscription?.status === 'canceled') {
			// If no current_period_end, treat as expired (show banner)
			if (!subscription.current_period_end) {
				return false;
			}
			const now = new Date();
			const periodEnd = new Date(subscription.current_period_end);
			return periodEnd > now; // Still within paid period (true = still active, false = expired)
		}
		return false;
	};

	// Handle different subscription states
	if (!subscription && !isTrialing) {
		return (
			<div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
				<h2 className="text-2xl font-semibold text-blue-900 mb-3">Choose Your Plan</h2>
				<p className="text-blue-800 font-medium mb-1">You don't have an active subscription yet.</p>
				<p className="text-blue-700 text-sm">
					Select a plan below to unlock all features and start managing your inventory efficiently.
				</p>
			</div>
		);
	}

	// Trial state (when user is in trial but has no subscription)
	if (isTrialing && !subscription) {
		return (
			<div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
				<h2 className="text-2xl font-semibold text-green-900 mb-3">🎉 Free Trial Active</h2>
				<p className="text-green-800 font-medium mb-1">
					You're currently on a free trial with access to starter plan features.
				</p>
				<p className="text-green-700 text-sm">Choose a plan below to continue after your trial ends.</p>
			</div>
		);
	}

	// Cancelled state - only show banner if the subscription period has fully expired
	if (subscription && subscription.status === 'canceled' && !isCancelledButActive()) {
		return (
			<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
				<p className="text-yellow-800 font-medium mb-1">Your subscription has been cancelled.</p>
				<p className="text-yellow-700 text-sm">Choose a new plan below to reactivate your premium features.</p>
			</div>
		);
	}

	// Active, pending_downgrade, or cancelled but still within paid period
	if (
		subscription &&
		(subscription.status === 'active' || subscription.status === 'pending_downgrade' || isCancelledButActive())
	) {
		const currentPlan = findCurrentPlan();
		const planName = currentPlan?.display_name || 'Starter Plan';
		const planType = currentPlan?.name || 'starter';
		const gradientClass = getPlanGradient(planType);

		// Use real usage data from subscription or fallback to 0
		const currentSales = subscription.usage?.sales || 0;
		const currentProducts = subscription.usage?.products || 0;
		const currentTeamMembers = subscription.usage?.teamMembers || 0;

		return (
			<div
				className={`bg-white rounded-lg px-0 py-8 md:p-8 md:px-0 mb-8 transition-all duration-500 ease-in-out overflow-hidden ${
					showPlans ? 'max-h-[3500px] md:max-h-[2000px]' : 'max-h-[800px] md:max-h-[400px]'
				}`}
			>
				<div className="mb-4">
					<span className="flex flex-row justify-between items-center">
						<h2 className="text-3xl font-semibold mb-2">
							<span className={`${gradientClass} bg-clip-text text-transparent`}>{planName}</span>
						</h2>

						{subscription.status !== 'canceled' && !subscription.is_cancelling && (
							<CreateButton
								key="cancel-subscription-button"
								onClick={onCancelClick}
								plain
								style={{
									color: '#DC2626',
									borderColor: '#DC2626',
									paddingRight: '0px',
								}}
							>
								Cancel Subscription
							</CreateButton>
						)}
					</span>

					<div className="text-gray-600">
						<span className="text-sm">
							{(subscription.status === 'canceled' || subscription?.is_cancelling) &&
							new Date(subscription.current_period_end) > new Date() ? (
								<div className="flex items-center gap-2 flex-wrap">
									<span className="text-red-600">
										Plan active till {formatDateInUserTimezone(subscription.current_period_end)}
									</span>
									<button
										onClick={onReactivate}
										className="text-blue-600 hover:text-blue-800 underline font-medium"
									>
										Reactivate Subscription
									</button>
								</div>
							) : (subscription.status === 'canceled' || subscription?.is_cancelling) &&
							  new Date(subscription.current_period_end) <= new Date() ? (
								<div className="flex items-center gap-2">
									<span className="text-red-600">
										Expired {formatDateInUserTimezone(subscription.current_period_end)}
									</span>
									<button
										onClick={onReactivate}
										className="text-blue-600 hover:text-blue-800 underline font-medium"
									>
										Resubscribe
									</button>
								</div>
							) : subscription.status === 'pending_downgrade' &&
							  subscription.pending_plan_display_name &&
							  subscription.next_billing_date ? (
								<div className="flex items-center gap-2 flex-wrap">
									<span>
										Scheduled to downgrade to {subscription.pending_plan_display_name} (
										{subscription.pending_plan_billing_interval === 'annually'
											? 'yearly'
											: 'monthly'}
										) on {formatDateInUserTimezone(subscription.next_billing_date)}
									</span>
									<button
										onClick={onCancelDowngrade}
										className="text-blue-600 hover:text-blue-800 underline font-medium"
									>
										Cancel Downgrade
									</button>
								</div>
							) : subscription?.next_billing_date ? (
								(() => {
									const periodEndDate = new Date(subscription.current_period_end);
									const now = new Date();
									const isExpired = periodEndDate < now;

									if (isExpired) {
										return (
											<div className="flex items-center gap-2">
												<span className="text-red-600">
													Expired {formatDateInUserTimezone(subscription.current_period_end)}
												</span>
												<button
													onClick={onReactivate}
													className="text-blue-600 hover:text-blue-800 underline font-medium"
												>
													Resubscribe
												</button>
											</div>
										);
									}

									return `Renews ${formatDateInUserTimezone(subscription.next_billing_date)}`;
								})()
							) : (
								'N/A'
							)}
						</span>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
					<UsageCard
						icon={
							<div style={{ fill: '#4B5563' }}>
								<OrderIcon width={20} height={20} />
							</div>
						}
						label="Sales per month"
						current={currentSales}
						limit={currentPlan?.sales_limit || null}
						planGradient={gradientClass}
					/>

					<UsageCard
						icon={
							<div style={{ fill: '#4B5563' }}>
								<ProductIcon width={20} height={20} />
							</div>
						}
						label="Products"
						current={currentProducts}
						limit={currentPlan?.products_limit || null}
						planGradient={gradientClass}
					/>

					<UsageCard
						icon={
							<div style={{ fill: '#4B5563' }}>
								<UsersIcon width={20} height={20} />
							</div>
						}
						label="Team Members"
						current={currentTeamMembers}
						limit={currentPlan?.team_members_limit || null}
						planGradient={gradientClass}
					/>
				</div>
			</div>
		);
	}

	// Past due state
	if (subscription && subscription.status === 'past_due') {
		return (
			<div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
				<h2 className="text-2xl font-semibold text-red-900 mb-3">Payment Failed</h2>
				<p className="text-red-800 font-medium mb-1">
					Your subscription payment has failed and your account is past due.
				</p>
				<p className="text-red-700 text-sm">
					Please update your payment method or choose a new plan below to restore access to premium features.
				</p>
			</div>
		);
	}

	// Default fallback
	return null;
};

export default CurrentSubscription;
